import { useIsMutating } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { CREATE_BOOKING_KEY } from '@/entities/booking'
import { Button, Dialog, Icon } from '@/shared/ui'
import { NewBookingForm } from './NewBookingForm'

/**
 * Кнопка «+ Новая бронь» вместе с модалкой, которую она открывает.
 *
 * ⚠️ МОДАЛКА НЕ В SEARCH-ПАРАМЕТРЕ, хотя карточка брони — именно там, и это не забывчивость.
 * `?booking=bk_1` называет РЕСУРС, которым делятся ссылкой; ссылка на пустую форму восстанавливает
 * только пустоту. «Назад» у карточки фича, а у формы — молча стёртая несохранённая работа. И второй
 * глобальный параметр обложил бы налогом каждую навигацию: `booking` уже требует, чтобы КАЖДАЯ смена
 * состояния шла через `search: (prev) => ({...prev, …})`.
 *
 * Состояние `open` живёт здесь, а форма — в детях `Dialog`, которых Radix не монтирует, пока модалка
 * закрыта: поля, ключ идемпотентности и наблюдатель мутации умирают вместе с закрытием.
 *
 * ⚠️ ПОСЛЕ УСПЕХА ОТКРЫВАЕМ КАРТОЧКУ: ответ `201 BookingDetail` уже лёг в её кеш, поэтому она
 * появляется в том же кадре без запроса. Просто закрыть модалку значило бы оставить менеджера в
 * тишине — свежая бронь может не попасть ни в текущий фильтр, ни на первую страницу, и «получилось?»
 * останется без ответа.
 *
 * ⚠️ Навигация ФУНКЦИЕЙ search: голый объект стёр бы `status`/`sort` того экрана, с которого бронь
 * создавали — TanStack Router search не сливает, а ЗАМЕНЯЕТ целиком.
 */
export function NewBookingButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  // Пока создание летит, модалка не закрывается: с формой ушёл бы ключ идемпотентности, и повтор
  // из новой формы — новый ключ, то есть вторая бронь.
  const creating = useIsMutating({ mutationKey: CREATE_BOOKING_KEY }) > 0

  return (
    <>
      <Button
        variant="primary"
        size="md"
        className={className}
        onClick={() => setOpen(true)}
      >
        <Icon name="plus" size={13} strokeWidth={2.2} />
        Новая бронь
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && creating) return
          setOpen(next)
        }}
        icon="plus"
        title="Новая бронь"
        description="Создание брони вручную"
      >
        <NewBookingForm
          onCreated={(booking) => {
            setOpen(false)
            void navigate({ to: '.', search: (prev) => ({ ...prev, booking: booking.id }) })
          }}
          onCancel={() => setOpen(false)}
        />
      </Dialog>
    </>
  )
}
